/* eslint-disable no-useless-escape */
import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { parse, ParseResult } from "papaparse";
import SVG from "react-inlinesvg";
import ProgressBar from "react-bootstrap/ProgressBar";
import _forEach from "lodash/forEach";
import _every from "lodash/every";
import Button from "../../../Components/Button/Button";
import CSVErrorDisplay from "./Modals/CSVErrorDisplay";
import { orderActions } from "../../../Store/Order/reducer";
import { AddPatientLeadModel, PatientInfo } from "../../../Services/Models/Order/OrderModel";
import { checkDuplicateRowsInCSV } from "../../../Methods/CSVProcessor/CheckDuplicateRowsInCSV";
import { sanitizeDiseaseString } from "../../../Methods/CSVProcessor/SanitizeDisease";
import { checkIsMissingHeaders } from "../../../Methods/CSVProcessor/CheckMissingHeaders";
import { nameRegExp, zipRegExp, mobileRegExpUS, USStateRegExp, cityNameRegExp } from "../../../Methods/Helpers/RegularExpressions";
import { selectUser } from "../../../Store/Provider/selectors";
// import { OrderInsertionInput } from "../../../API";

const checkIfRowIsEmpty = (obj: object) => {
  return _every(obj, (value) => value === null || value === undefined || value === "");
}

const AddPatientCSV: React.FC<any> = ({ importComplete, setIsFileSelected, ...args }: any) => {
  const dispatch = useAppDispatch();
  const providerData = useAppSelector(selectUser);
  const fileRef = useRef<any>();
  const [CSVFiles, setCSVFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [parsedCSVData, setParsedCSVData] = useState<AddPatientLeadModel[]>([]);
  // const [parsedMultiCSVData, setParsedMultiCSVData] = useState<OrderInsertionInput[]>([]);
  const [errorCSVData, setErrorCSVData] = useState<any>([]);
  const [isValidatingCSV, setIsValidatingCSV] = useState<boolean>(false);
  const [isValidatedCSV, setIsValidatedCSV] = useState<boolean>(false);
  const [isCSVErrorDetails, setIsCSVErrorDetails] = useState<boolean>(false);

  //CSV Field validation 
  const validationSchema = Yup.object().shape({
    PARENT_FIRST_NAME: Yup.string().required("Missing Information").matches(nameRegExp, "Invalid Character").min(1, "Minimum 1 characters are required").max(40, "Max 15 characters are allowed").nullable(),
    PARENT_LAST_NAME: Yup.string().required("Missing Information").matches(nameRegExp, "Invalid Character").min(1, "Minimum 1 characters are required").max(40, "Max 15 characters are allowed").nullable(),
    PARENT_EMAIL: Yup.string().email("Invalid Format").required("Missing Information").nullable(),
    PARENT_PHONE_NUMBER: Yup.string().matches(mobileRegExpUS, "Invalid Format").required("Missing Information").nullable(),
    CHILD_FIRST_NAME: Yup.string().required("Missing Information").matches(nameRegExp, "Invalid Character").min(1, "Minimum 1 characters are required").max(40, "Max 15 characters are allowed").nullable(),
    ADDRESS_LINE_1: Yup.string().required("Address is required").min(10, "Minimum 10 characters required").max(80, "Max 80 character are allowed").matches(/^(?![ ]+$)[A-Za-z0-9'\.\-\s\,]+$/, "Invalid Character").nullable(),
    ADDRESS_LINE_2: Yup.string().nullable().min(2, "Minimum 2 characters required").max(80, "Max 80 character are allowed").matches(/^(?![ ]+$)[A-Za-z0-9'\.\-\s\,]+$/, "Invalid Character"),
    CITY: Yup.string().required("City is required").matches(cityNameRegExp, "Invalid Characters").max(30, "Max 30 characters allowed").nullable(),
    STATE_CODE: Yup.string().required("State is required").matches(USStateRegExp, "Invalid Information").nullable(),
    ZIP_CODE: Yup.string().matches(zipRegExp, "Invalid zip code").max(5, "Only 5 digits allowed").required("Zip code is required").nullable(),
  });

  //CSV Field validation function
  function validateField(field: string, value: any) {
    try {
      const schema = Yup.object().shape({
        [field]: validationSchema.fields[field],
      });
      schema.validateSync({ [field]: value });
      return true
    } catch (error: any) {
      return error.message;
    }
  }

  const processCSVHeader = (str: any, delim = ',') => {
    const headers = str.slice(0, str.indexOf('\n')).split(delim);
    headers[headers.length - 1] = headers[headers.length - 1].replace(/(\r\n|\n|\r)/gm, "");
    return headers;
  }

  const onFileSelect = ({ currentTarget: { files } }: React.ChangeEvent<HTMLInputElement>) => {
    if (files && files.length) {
      setIsFileSelected(true);
      let patientInfoArray = ["PARENT_FIRST_NAME", "PARENT_LAST_NAME", "PARENT_EMAIL", "PARENT_PHONE_NUMBER", "CHILD_FIRST_NAME", "ADDRESS_LINE_1", "ADDRESS_LINE_2", "CITY", "STATE_CODE", "ZIP_CODE"];
      _forEach(files, (el: any, index) => {
        let missingHeadersInCSV: Array<string | []> = [];
        if (el.type === "text/csv") {
          setIsValidatingCSV(true);
          setCSVFiles((patient) => [...patient, el]);
          var totalFile = files.length;
          var tempProgress = 0;
          var parsedCSVRows: any = [];
          const invalidRows: any = [];
          if (totalFile > 1) {
            if (index + 1 === totalFile) {
              tempProgress = 100;
            } else {
              tempProgress = Math.round((index / totalFile) * 100);
            }
          }

          let csvHeaders: any | undefined = [];
          const reader = new FileReader();
          reader.onload = function (e) {
            const text = e.target?.result;
            csvHeaders = processCSVHeader(text);
            const { isMissingHeaders, missingHeaders } = checkIsMissingHeaders(csvHeaders); // Check if valid file is provided by checking if required headers in the file exist
            missingHeadersInCSV.push(...missingHeaders);
            if (!isMissingHeaders) {
              parse(el, {
                header: true,
                dynamicTyping: true,
                skipEmptyLines: true,
                step: function (results: ParseResult<Record<string, unknown>>, parser) {
                  let ifRowIsEmpty = checkIfRowIsEmpty(results.data);

                  if (totalFile === 1) {
                    var progress = results.meta.cursor;
                    var newPercent = Math.round((progress / el.size) * 100);
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    if (newPercent === percent) return;
                    var percent: any = newPercent;
                    setProgress(percent);
                  }

                  if (!ifRowIsEmpty) {
                    let patient: PatientInfo | {} = {};
                    let isValidRow: boolean = true;
                    let invalidField: any = [];
                    let medicalCondition: any = [];

                    // eslint-disable-next-line array-callback-return
                    Object.keys(results.data).map((rowKey: any) => {
                      // let medic: Array<string> = [];
                      if (patientInfoArray.includes(rowKey)) {
                        if (validateField(rowKey, results.data[rowKey]) !== true) {
                          isValidRow = false;
                          invalidField.push(rowKey);
                        } else {
                          patient = { ...patient, [rowKey]: results.data[rowKey] };
                        }
                      }

                      // if (rowKey === "medicalCondition") {
                      //   const sanitizedDiseaseArray: Array<string> = sanitizeDiseaseString(results.data[rowKey]);
                      //   medicalCondition = sanitizedDiseaseArray;
                      //   if (validateField("medicalCondition", sanitizedDiseaseArray) !== true) {
                      //     isValidRow = false;
                      //     invalidField.push('medicalCondition');
                      //   } else {
                      //     if (sanitizedDiseaseArray.length > 1) {
                      //       isValidRow = false;
                      //       invalidField.push('medicalCondition');
                      //     } else {
                      //       patient = { ...patient, medicalCondition: sanitizedDiseaseArray };
                      //       // medic = _split(`${results.data[rowKey]}`, ",");
                      //       // medic.map((element) => element.trim());
                      //       // patient = { ...patient, medicalCondition: medic };
                      //     }
                      //   }
                      // }

                      // if (emergencyContactInfoArray.includes(rowKey)) {
                      //   if (validateField(rowKey, results.data[rowKey]) !== true) {
                      //     isValidRow = false;
                      //     invalidField.push(rowKey);
                      //   } else {
                      //     emergencyContact = { ...emergencyContact, [rowKey]: results.data[rowKey] };
                      //   }
                      // }

                    });

                    if (isValidRow) {
                      parsedCSVRows.push(patient);
                    } else {
                      let invalidData: object = {
                        ...results.data,
                        medicalCondition,
                        invalidField,
                        errorType: invalidField.length > 1 ? "Multiple Errors" : validateField(invalidField[0],
                          invalidField[0] === "medicalCondition" ? sanitizeDiseaseString(results.data[invalidField[0]]) : results.data[invalidField[0]])
                      };
                      invalidRows.push(invalidData);
                    }

                    let duplicateCheckedParsedCSVRows = checkDuplicateRowsInCSV(parsedCSVRows);
                    setParsedCSVData(duplicateCheckedParsedCSVRows);
                    // setParsedMultiCSVData(duplicateCheckedParsedCSVRows)
                    let duplicateCheckedInvalidCSVRows = checkDuplicateRowsInCSV(invalidRows);
                    setErrorCSVData(duplicateCheckedInvalidCSVRows.length > 0 ? duplicateCheckedInvalidCSVRows : []);
                  }
                },
              });
            } else {
              setIsValidatingCSV(false);
              setProgress(100);
              toast.error(`! Error... INVALID_FILE_FORMAT, \r\n REQUIRED_COLUMN_ERROR, ! Aborting Process`);
              onFileClear();
            }
          }
          reader.readAsText(el);
          let percent = tempProgress;
          setProgress(percent);
        } else {
          toast.warning(`${el.name} is not a valid CSV file.`);
        }
      });
    }
  };

  const onFileClear = () => {
    setIsFileSelected(false);
    setCSVFiles([]);
    setProgress(0);
    fileRef.current?.clearValue();
    setParsedCSVData([]);
    // setParsedMultiCSVData([])
    setErrorCSVData([]);
  };

  // Open & close Error Modal
  const OpenErrorModal = () => {
    setIsCSVErrorDetails(true);
  };

  //CSV Import Button Click 
  const onImport = () => {
    let currentProvider = providerData
    let parsedDataTemp: any = parsedCSVData.map((el: any) => {
      return {
        ecFirstName: el.PARENT_FIRST_NAME.replace(/\b\w/g, (char: string) => char.toUpperCase()),
        ecLastName: el.PARENT_LAST_NAME.replace(/\b\w/g, (char: string) => char.toUpperCase()),
        ecEmail: el.PARENT_EMAIL.toLowerCase(),
        ecMobile: el.PARENT_PHONE_NUMBER,
        firstname: el.CHILD_FIRST_NAME.replace(/\b\w/g, (char: string) => char.toUpperCase()),
        address: el.ADDRESS_LINE_1,
        address2: el.ADDRESS_LINE_2,
        city: el.CITY,
        state: el.STATE_CODE,
        country: "USA",
        zipcode: el.ZIP_CODE,
        provider_tag: currentProvider["custom:orgName"]?.toLowerCase() || "kiddo",
        order_origin: "ProviderPortal",
        sku: ""
      }
    });
    dispatch(orderActions.addPatientLeadOrder(parsedDataTemp));
    setIsValidatedCSV(false);
    onFileClear();
    importComplete();
  };
  useEffect(() => {
    if (progress === 100) {
      setTimeout(() => {
        setIsValidatingCSV(false);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  const closeHandelError = () => {
    setIsCSVErrorDetails(false);
  };

  return (
    <>
      {isCSVErrorDetails && (
        <CSVErrorDisplay errorData={errorCSVData} validateField={validateField} closeHandelError={closeHandelError} />
      )}

      {!isValidatedCSV && (
        <>
          {errorCSVData !== undefined && errorCSVData.length > 0 && !isValidatingCSV && (
            <>
              <div className="error-box">
                <div className="d-flex align-items-center mb-2 position-relative">
                  <span className="icon">
                    <SVG src="Assets/Images/Icons/error-minus.svg" />
                  </span>
                  <h5 className="mb-0 fs-16">Error</h5>
                </div>
                <p className="font-500 fs-16 mb-2">
                  To resolve the issue, please update patient data and upload again
                </p>
                <div className="row">
                  <div className="col w-100">
                    <ul className="ps-3 mb-0">
                      <li className="mb-2">
                        {errorCSVData.length} patient records in the file have one or more validation errors
                      </li>
                    </ul>
                  </div>
                  <div className="col-auto text-end">
                    <button
                      type="button"
                      className="btn me-3 error-text fs-14 p-0 w-auto text-decoration-underline"
                      onClick={onFileClear}>
                      Clear All Files
                    </button>
                    <button type="button" className="btn me-3 error-text fs-14 p-0 w-auto text-decoration-underline" onClick={OpenErrorModal} >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {isValidatingCSV && (
            <>
              <ProgressBar
                variant="success"
                now={progress}
              />
            </>
          )}

          <div className="upload-btn-wrapper">
            {CSVFiles.length > 0 ? (
              <>
                {isValidatingCSV ? (
                  <>
                    <div className="upload-zone upload-zone-uploading file-center">
                      <div className="flex justify-content-center file-inner">
                        <div className="upload-icon mx-auto">
                          <img src="Assets/Images/Icons/upload.svg" alt="upload" width="60" height="80" />
                          <span className="d-block upload-text">
                            Uploading...
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {errorCSVData !== undefined && errorCSVData.length > 0 ? (
                      <>
                        <div className="upload-zone upload-zone-error added-file-zone">
                          <div className="flex justify-content-center file-inner">
                            {CSVFiles.map((el, idx) => {
                              return (
                                <>
                                  <div key={idx} className="upload-file d-flex flex-column">
                                    <img src="Assets/Images/Icons/csv-files.svg" className="mx-auto mb-2" alt="upload" width="60" height="80" />
                                    <span className="d-block patient-name text-break">{el.name.split(".")[0]}</span>
                                  </div>
                                </>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="upload-zone upload-zone-success file-center">
                          <div className="flex justify-content-center file-inner">
                            {CSVFiles.map((el, idx) => {
                              return (
                                <>
                                  <div key={idx} className="upload-file d-flex flex-column">
                                    <img src="Assets/Images/Icons/csv-files.svg" className="mx-auto mb-2" alt="upload" width="60" height="80" />
                                    <span className="d-block patient-name text-break">{el.name.split(".")[0]}</span>
                                    <div className="d-flex align-items-center upload-text">
                                      <SVG className="tick-icon mr-8" src="Assets/Images/Icons/tick-icon.svg" width="16" height="16" />
                                      <span className="fw-normal">
                                        Patients list validated successfully
                                      </span>
                                    </div>
                                  </div>
                                </>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <div className="upload-zone upload-zone-upload file-center">
                  <div className="text mb-2 w-100">
                    Drops the CSV files here
                  </div>
                  <div className="or mb-2 w-100">Or</div>
                  <div className="uploadcsvs position-relative d-inline-flex">
                    <div className="mx-auto d-flex justify-content-center align-items-center position-relative">
                      <span className="btn">
                        Upload CSV files
                      </span>
                    </div>
                  </div>
                  <input
                    type="file"
                    name="patientFiles[]"
                    // multiple
                    ref={fileRef}
                    accept=".csv"
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => onFileSelect(e)}
                  />
                </div>
              </>
            )}
          </div>
          <div className="text-center max-auto">
            {/* {isUploading && (
              <>
                <Button 
                  btntype="button" 
                  customClass="max-w-381 text-bold-500 mt-4 mx-2" 
                  primary={false} 
                  text={true} 
                  label="Cancel"
                  onClick={onFileClear} />
              </>
            )} */}
            {errorCSVData !== undefined && errorCSVData?.length > 0 && !isValidatingCSV ? (
              <>
                {/* <Button 
                    btntype="button" 
                    customClass="max-w-381 text-bold-500 mt-4 mx-2" 
                    primary={true} 
                    text={true} 
                    label="Clear All Files"
                    onClick={onFileClear} /> */}
              </>
            ) : (
              <>
                {!isValidatingCSV && CSVFiles?.length > 0 && (
                  <>
                    <Button
                      btnType="button"
                      customClass="max-w-381 text-bold-500 mt-4 mx-2"
                      primary={false}
                      text={true}
                      label="Delete & import again"
                      onClick={onFileClear} />
                    <Button
                      btnType="button"
                      customClass={`btn max-w-381 text-bold-500 mt-4 mx-2`}
                      primary={true}
                      text={true}
                      disabled={CSVFiles?.length > 0 ? false : true}
                      onClick={onImport}
                      label="Continue to order" />
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AddPatientCSV;
